import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { FiSearch, FiPlus, FiUpload, FiX } from 'react-icons/fi';
import { 
  getRecipes, 
  createRecipe, 
  updateRecipe, 
  deleteRecipe, 
  bulkUploadRecipes
} from '../../api';
import RecipeCard from './RecipeCard';
import RecipeForm from './RecipeForm';
import { setAuthToken } from '../../auth';

const Recipes = ({ currency, setCurrency }) => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [ingredientSuggestions, setIngredientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIngredientIndex, setActiveIngredientIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  // const [currency, setCurrency] = useState({
  //   symbol: '₹',
  //   code: 'INR',
  //   exchangeRate: 1,
  //   locale: 'en-IN'
  // });

  // Fetch recipes on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const recipesData = await getRecipes();
        
        const normalizedRecipes = recipesData.map(recipe => {
          let ingredients = [];
          try {
            ingredients = typeof recipe.ingredients === 'string' ? 
              JSON.parse(recipe.ingredients) : 
              (recipe.ingredients || []);
          } catch (e) {
            console.error('Error parsing ingredients:', e);
            ingredients = [];
          }
          
          return {
            ...recipe,
            preparationTime: recipe.preparation_time || recipe.preparationTime,
            expiryDuration: recipe.expiry_duration || recipe.expiryDuration,
            cost: parseFloat(recipe.cost || 0),
            ingredients: ingredients
          };
        });
        
        setRecipes(normalizedRecipes);
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to load recipes. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter recipes based on search term
  const filteredRecipes = recipes.filter(recipe => 
    recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExcelUpload = async (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      try {
        setIsSubmitting(true);
        const response = await bulkUploadRecipes(file);
        
        try {
          const recipesData = await getRecipes();
          const normalizedRecipes = recipesData.map(recipe => ({
            ...recipe,
            preparationTime: recipe.preparation_time || recipe.preparationTime,
            expiryDuration: recipe.expiry_duration || recipe.expiryDuration,
            cost: parseFloat(recipe.cost || 0),
            ingredients: typeof recipe.ingredients === 'string' ? 
              JSON.parse(recipe.ingredients) : 
              recipe.ingredients || []
          }));
          setRecipes(normalizedRecipes);
        } catch (fetchError) {
          console.error('Error refreshing recipes:', fetchError);
        }
        
        alert('Recipes imported successfully!');
      } catch (err) {
        console.error('Upload Error:', err);
        alert(`Error uploading Excel: ${err.response?.data?.detail || err.message}`);
      } finally {
        setIsSubmitting(false);
        setSelectedFile(null);
        setFileInputKey(Date.now());
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileInputKey(Date.now());
  };

  const openAddForm = () => {
    setCurrentRecipe({
      name: '',
      description: '',
      preparationTime: '',
      cost: '',
      expiryDuration: '',
      instructions: '',
      ingredients: [{ name: '', quantity: '', unit: 'kg' }],
      image_url: null
    });
    setShowForm(true);
  };

  const openEditForm = (recipe) => {
    setCurrentRecipe({
      ...recipe,
      ingredients: recipe.ingredients.length > 0 ? [...recipe.ingredients] : [{ name: '', quantity: '', unit: 'kg' }]
    });
    setShowForm(true);
  };

  const handleDeleteRecipe = async (id) => {
    if (window.confirm('Are you sure you want to delete this recipe?')) {
      try {
        await deleteRecipe(id);
        setRecipes(recipes.filter(recipe => recipe.id !== id));
      } catch (error) {
        console.error('Error deleting recipe:', error);
        alert(`Error: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecipe(prev => ({ ...prev, [name]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    setCurrentRecipe(prev => {
      const updatedIngredients = [...prev.ingredients];
      updatedIngredients[index][field] = value;
      return { ...prev, ingredients: updatedIngredients };
    });

    if (field === 'name' && value.length > 0) {
      setActiveIngredientIndex(index);
      const mockSuggestions = [
        'Chicken', 'Rice', 'Tomato', 'Onion', 'Garlic',
        'Ginger', 'Potato', 'Carrot', 'Bell Pepper', 'Broccoli'
      ].filter(item => item.toLowerCase().includes(value.toLowerCase()));
      setIngredientSuggestions(mockSuggestions);
      setShowSuggestions(true);
    } else if (field !== 'name') {
      setShowSuggestions(false);
    }
  };

  const addIngredient = () => {
    setCurrentRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: '', unit: 'kg' }]
    }));
    setTimeout(() => {
      const element = document.getElementById(`ingredient-${currentRecipe.ingredients.length}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const removeIngredient = (index) => {
    if (currentRecipe.ingredients.length > 1) {
      setCurrentRecipe(prev => {
        const updatedIngredients = [...prev.ingredients];
        updatedIngredients.splice(index, 1);
        return { ...prev, ingredients: updatedIngredients };
      });
    }
  };

  const selectSuggestion = (suggestion) => {
    setCurrentRecipe(prev => {
      const updatedIngredients = [...prev.ingredients];
      updatedIngredients[activeIngredientIndex].name = suggestion;
      return { ...prev, ingredients: updatedIngredients };
    });
    setShowSuggestions(false);
  };

  const resetForm = () => {
    setShowForm(false);
    setCurrentRecipe(null);
  };

  const handleAddOrUpdateRecipe = async (formData, id = null) => {
    setIsSubmitting(true);
    try {
      let response;
      if (id) {
        const normalizedData = new FormData();
        for (const [key, value] of formData.entries()) {
          const normalizedKey = key
            .replace('preparationTime', 'preparation_time')
            .replace('expiryDuration', 'expiry_duration');
          normalizedData.append(normalizedKey, value);
        }
        
        response = await updateRecipe(id, normalizedData);
        setRecipes(recipes.map(r => r.id === parseInt(id) ? {
          ...r,
          ...response,
          preparationTime: response.preparation_time,
          expiryDuration: response.expiry_duration,
          cost: parseFloat(response.cost || 0)
        } : r));
      } else {
        const normalizedData = new FormData();
        for (const [key, value] of formData.entries()) {
          const normalizedKey = key
            .replace('preparationTime', 'preparation_time')
            .replace('expiryDuration', 'expiry_duration');
          normalizedData.append(normalizedKey, value);
        }
        
        response = await createRecipe(normalizedData);
        setRecipes([...recipes, {
          ...response,
          preparationTime: response.preparation_time,
          expiryDuration: response.expiry_duration,
          cost: parseFloat(response.cost || 0)
        }]);
      }
      setShowForm(false);
      setCurrentRecipe(null);
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout currency={currency} setCurrency={setCurrency}>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="relative w-full md:w-64">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search recipes..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            <input
              key={fileInputKey}
              type="file"
              id="excel-upload"
              accept=".xlsx, .xls"
              className="hidden"
              onChange={handleExcelUpload}
            />
            <label
              htmlFor="excel-upload"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <FiUpload /> Upload Excel
            </label>
            
            {selectedFile && (
              <div className="flex items-center bg-gray-100 rounded-lg px-3 py-1">
                <span className="text-sm font-medium truncate max-w-[120px]">
                  {selectedFile.name}
                </span>
                <button
                  onClick={handleRemoveFile}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                  aria-label="Remove file"
                >
                  <FiX size={16} />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={openAddForm}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <FiPlus /> Add Recipe
          </button>
        </div>
      </div>

      {loading ? (
        <div className="w-full text-center py-8">Loading recipes...</div>
      ) : error ? (
        <div className="w-full text-center py-8 text-red-500">Error loading recipes: {error}</div>
      ) : filteredRecipes.length > 0 ? (
        <div className="flex flex-wrap -mx-1">
          {filteredRecipes.map(recipe => (
            <div key={recipe.id} className="w-full sm:w-1/2 px-1 mb-4">
              <RecipeCard 
                recipe={recipe} 
                currency={currency}
                onEdit={() => openEditForm(recipe)}
                onDelete={() => handleDeleteRecipe(recipe.id)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full text-center py-8 text-gray-500">
          No recipes found. Try a different search or add a new recipe.
        </div>
      )}

      {showForm && currentRecipe && (
        <RecipeForm
          recipeData={currentRecipe}
          currency={currency}
          onInputChange={handleInputChange}
          onIngredientChange={handleIngredientChange}
          onAddIngredient={addIngredient}
          onRemoveIngredient={removeIngredient}
          onSelectSuggestion={selectSuggestion}
          onCancel={resetForm}
          onSubmit={handleAddOrUpdateRecipe}
          ingredientSuggestions={ingredientSuggestions}
          showSuggestions={showSuggestions}
          activeIngredientIndex={activeIngredientIndex}
          setShowSuggestions={setShowSuggestions}
          setActiveIngredientIndex={setActiveIngredientIndex}
          setIngredientSuggestions={setIngredientSuggestions}
          isEditing={!!currentRecipe.id}
          isSubmitting={isSubmitting}
        />
      )}
    </Layout>
  );
};

export default Recipes;